import {SegmentedControl, createStyles} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {getUserMessage} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {GatewayFieldsForm, useGatewayFields} from './GatewayFieldsForm';
import {
  GENERIC_UPDATE_FAILURE_MESSAGE,
  GatewayType,
  InstrumentGateway,
  S3_FIELD_IDS,
  S3PermissionMode,
  stringFields,
} from './InstrumentGatewayConstants';
import {createGateway, updateGateway} from './InstrumentGatewayDAO';
import {getBucketPolicy, getGlobalRoleArn, getPermissionsPolicy, getTrustPolicy} from './S3PolicyTemplates';

const useStyles = createStyles((theme) => ({
  spacer: {
    marginTop: theme.spacing.sm,
  },
  permissionSelector: {
    marginBottom: theme.spacing.sm,
  },
  sectionLabel: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    fontSize: theme.fontSizes.sm,
    marginBottom: theme.spacing.xs,
  },
  readOnlyField: {
    marginTop: theme.spacing.xs,
  },
}));

function openPolicyTab(content: string): void {
  const blob = new Blob([content], {type: 'application/json'});
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener noreferrer');
  // Revoke after a delay so the opened tab has time to load before the URL is freed.
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 60000);
}

const SEGMENTED_CONTROL_DATA = [
  {value: S3PermissionMode.IAM_ROLE, label: 'IAM Role'},
  {value: S3PermissionMode.BUCKET_POLICY, label: 'Bucket Policy'},
];

interface ViewBucketInfoModalProps {
  gateway: InstrumentGateway;
  roleArn: string;
  onRequestClose: () => void;
}

export function ViewBucketInfoModal(props: ViewBucketInfoModalProps): JSX.Element {
  const {gateway, roleArn, onRequestClose} = props;
  const {fields} = useGatewayFields(GatewayType.S3);
  const bucketName = gateway.fields?.[S3_FIELD_IDS.CUSTOMER_BUCKET] ?? '';
  const isIamMode = Boolean(gateway.fields?.[S3_FIELD_IDS.CUSTOMER_ROLE_ARN]);

  const visibleFields = !isIamMode
    ? (fields ?? []).filter((f) => f.id !== S3_FIELD_IDS.CUSTOMER_ROLE_ARN)
    : (fields ?? []);

  return (
    <Modal
      buttons={[
        ...(isIamMode
          ? [
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate trust policy',
                onClick: () => {
                  openPolicyTab(getTrustPolicy());
                },
                submit: false,
              },
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate permissions policy',
                disabled: !bucketName,
                onClick: () => {
                  openPolicyTab(getPermissionsPolicy(bucketName));
                },
                submit: false,
              },
            ]
          : [
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate bucket policy',
                disabled: !bucketName,
                onClick: () => {
                  openPolicyTab(getBucketPolicy(bucketName, roleArn));
                },
                submit: false,
              },
            ]),
        {
          style: ButtonStyle.PRIMARY,
          text: 'Close',
          cancel: true,
        },
      ]}
      title="S3 Bucket information"
      onRequestClose={onRequestClose}
      closeable={true}
    >
      The following cloud gateway is connected to an AWS S3 bucket.
      <br />
      <br />
      <TextField label="Gateway name" value={gateway.name} disabled={true} onChange={() => {}} />
      <br />
      {fields == null ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <GatewayFieldsForm
            fields={visibleFields}
            initialValues={stringFields(gateway.fields)}
            disabled={true}
            onValuesChange={() => {}}
          />
          {isIamMode ? (
            <TextField
              label="Benchling role ARN"
              value={getGlobalRoleArn()}
              disabled={true}
              onChange={() => {}}
            />
          ) : (
            <TextField label="Benchling role ARN" value={roleArn} disabled={true} onChange={() => {}} />
          )}
        </>
      )}
    </Modal>
  );
}

interface CreateS3GatewayModalProps {
  roleArn: string;
  onRequestClose: () => void;
  onSuccess?: () => void;
}

const GENERIC_VALIDATION_FAILURE_MESSAGE = 'Validation failed. Please check your input and try again.';

export function CreateS3GatewayModal(props: CreateS3GatewayModalProps): JSX.Element {
  const {roleArn, onRequestClose, onSuccess} = props;
  const {classes} = useStyles();
  const [error, setError] = useState<{fieldId: string | null; message: string} | null>(null);
  const [permissionMode, setPermissionMode] = useState<S3PermissionMode>(
    BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD
      ? S3PermissionMode.IAM_ROLE
      : S3PermissionMode.BUCKET_POLICY
  );

  const [gatewayName, setGatewayName] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const {fields} = useGatewayFields(GatewayType.S3);
  // When the IAM role flag is off, hide customer_role_arn entirely.
  // When it's on but user selects Bucket Policy mode, also hide customer_role_arn.
  const isIamMode =
    BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD &&
    permissionMode === S3PermissionMode.IAM_ROLE;
  const visibleFields = isIamMode
    ? (fields ?? []).map((f) => (f.id === S3_FIELD_IDS.CUSTOMER_ROLE_ARN ? {...f, isRequired: true} : f))
    : (fields ?? []).filter((f) => f.id !== S3_FIELD_IDS.CUSTOMER_ROLE_ARN);

  const bucketName = fieldValues[S3_FIELD_IDS.CUSTOMER_BUCKET] || '';
  const allRequiredFieldsFilled =
    gatewayName.trim() &&
    (visibleFields ?? []).filter((f) => f.isRequired).every((f) => fieldValues[f.id]?.trim());

  return (
    <Modal
      buttons={[
        ...(permissionMode === S3PermissionMode.BUCKET_POLICY
          ? [
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate bucket policy',
                disabled: !bucketName,
                onClick: () => {
                  openPolicyTab(getBucketPolicy(bucketName.trim(), roleArn));
                },
                submit: false,
              },
            ]
          : [
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate trust policy',
                onClick: () => {
                  openPolicyTab(getTrustPolicy());
                },
                submit: false,
              },
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate permissions policy',
                disabled: !bucketName,
                onClick: () => {
                  openPolicyTab(getPermissionsPolicy(bucketName.trim()));
                },
                submit: false,
              },
            ]),
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !allRequiredFieldsFilled || fields == null,
          submit: true,
        },
      ]}
      title="Configure cloud gateway"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error.message} : null}
      onRequestSubmit={async () => {
        const submitFields: Record<string, string> = {};
        for (const field of visibleFields) {
          const value = fieldValues[field.id];
          if (value != null) {
            submitFields[field.id] = value.trim();
          }
        }
        setError(null);
        try {
          await withLoadingScreen(
            createGateway(gatewayName.trim(), 'S3', submitFields),
            'Setting up instrument gateway...'
          );
          onSuccess?.();
          onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (err) {
          const fieldId: string | null = err?.responseJSON?.field ?? null;
          setError({fieldId, message: getUserMessage(err) ?? GENERIC_VALIDATION_FAILURE_MESSAGE});
        }
      }}
    >
      <div>
        <div>
          <TextField
            label="Gateway name"
            isRequired={true}
            value={gatewayName}
            onChange={(e) => {
              setGatewayName(e.target.value);
              setError(null);
            }}
          />
        </div>
        {BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD ? (
          <div className={classes.spacer}>
            <div className={classes.sectionLabel}>Access method</div>
            <div className={classes.permissionSelector}>
              <SegmentedControl
                aria-label="Access method"
                data={SEGMENTED_CONTROL_DATA}
                value={permissionMode}
                onChange={(value: string) => {
                  setPermissionMode(value as S3PermissionMode);
                  setError(null);
                }}
                fullWidth={true}
              />
            </div>
            {permissionMode === S3PermissionMode.IAM_ROLE && (
              <div>
                To connect to your S3 bucket, provide your IAM role ARN and bucket name. Benchling will assume
                this role to access your bucket. Use &quot;Generate trust policy&quot; and &quot;Generate
                permissions policy&quot; to get the policies to apply to your IAM role.
              </div>
            )}
            {permissionMode === S3PermissionMode.BUCKET_POLICY && (
              <div>
                To connect to your S3 bucket, provide your bucket name and apply the generated policy to your
                bucket. This grants Benchling&apos;s role direct access.
              </div>
            )}
          </div>
        ) : (
          <div className={classes.spacer}>
            To connect to your S3 bucket, the following steps are required:
            <br />
            <br />
            1. Enter a Gateway Name for identification in Benchling
            <br />
            2. Provide your S3 Bucket Name
            <br />
            3. Generate and Apply the Policy to your Bucket
          </div>
        )}
        <div className={classes.spacer}>
          {fields == null ? (
            <CenteredHorizontalThrobber />
          ) : (
            <>
              <GatewayFieldsForm
                fields={visibleFields}
                errorFieldId={error?.fieldId}
                onValuesChange={(values) => {
                  setFieldValues(values);
                  setError(null);
                }}
              />
              <div className={classes.readOnlyField}>
                {isIamMode ? (
                  <TextField
                    label="Benchling role ARN"
                    value={getGlobalRoleArn()}
                    disabled={true}
                    onChange={() => {}}
                  />
                ) : (
                  <TextField label="Benchling role ARN" value={roleArn} disabled={true} onChange={() => {}} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

interface EditS3GatewayModalProps {
  gateway: InstrumentGateway;
  roleArn: string;
  onRequestClose: () => void;
  onSave: () => void;
}

export function EditS3GatewayModal(props: EditS3GatewayModalProps): JSX.Element {
  const {gateway, roleArn, onRequestClose, onSave} = props;
  const {classes} = useStyles();
  const [error, setError] = useState<{fieldId: string | null; message: string} | null>(null);

  const existingRoleArn = gateway.fields?.[S3_FIELD_IDS.CUSTOMER_ROLE_ARN];
  const [permissionMode, setPermissionMode] = useState<S3PermissionMode>(
    BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD && existingRoleArn
      ? S3PermissionMode.IAM_ROLE
      : S3PermissionMode.BUCKET_POLICY
  );

  const [gatewayName, setGatewayName] = useState(gateway.name);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const {fields} = useGatewayFields(GatewayType.S3);

  const isIamMode =
    BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD &&
    permissionMode === S3PermissionMode.IAM_ROLE;
  const visibleFields = isIamMode
    ? (fields ?? []).map((f) => (f.id === S3_FIELD_IDS.CUSTOMER_ROLE_ARN ? {...f, isRequired: true} : f))
    : (fields ?? []).filter((f) => f.id !== S3_FIELD_IDS.CUSTOMER_ROLE_ARN);

  const bucketName = fieldValues[S3_FIELD_IDS.CUSTOMER_BUCKET] || '';
  const allRequiredFieldsFilled =
    gatewayName.trim() &&
    (visibleFields ?? []).filter((f) => f.isRequired).every((f) => fieldValues[f.id]?.trim());

  return (
    <Modal
      buttons={[
        ...(permissionMode === S3PermissionMode.BUCKET_POLICY
          ? [
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate bucket policy',
                disabled: !bucketName,
                onClick: () => {
                  openPolicyTab(getBucketPolicy(bucketName.trim(), roleArn));
                },
                submit: false,
              },
            ]
          : [
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate trust policy',
                onClick: () => {
                  openPolicyTab(getTrustPolicy());
                },
                submit: false,
              },
              {
                style: ButtonStyle.PRIMARY,
                text: 'Generate permissions policy',
                disabled: !bucketName,
                onClick: () => {
                  openPolicyTab(getPermissionsPolicy(bucketName.trim()));
                },
                submit: false,
              },
            ]),
        {
          style: ButtonStyle.SECONDARY,
          text: 'Save',
          disabled: !allRequiredFieldsFilled || fields == null,
          submit: true,
        },
      ]}
      title="Edit gateway"
      onRequestClose={onRequestClose}
      closeable={true}
      bannerProps={error != null ? {bannerType: InlineBannerType.ERROR, text: error.message} : null}
      onRequestSubmit={async () => {
        const trimmedName = gatewayName.trim();
        if (!trimmedName) {
          setError({fieldId: null, message: 'Name is required'});
          return;
        }
        const submitFields: Record<string, string> = {};
        for (const field of visibleFields) {
          const value = fieldValues[field.id];
          if (value != null) {
            submitFields[field.id] = value.trim();
          }
        }
        // When switching from IAM Role to Bucket Policy, explicitly clear
        // customer_role_arn so the backend overwrites the stored value.
        if (permissionMode === S3PermissionMode.BUCKET_POLICY && existingRoleArn) {
          submitFields[S3_FIELD_IDS.CUSTOMER_ROLE_ARN] = '';
        }
        setError(null);
        try {
          await withLoadingScreen(
            updateGateway(gateway.id, trimmedName, submitFields),
            'Updating gateway...'
          );
          onSave();
          onRequestClose();
          // eslint-disable-next-line benchlint/always-throw-from-catch
        } catch (err) {
          const fieldId: string | null = err?.responseJSON?.field ?? null;
          setError({fieldId, message: getUserMessage(err) ?? GENERIC_UPDATE_FAILURE_MESSAGE});
        }
      }}
    >
      <div>
        <div>
          <TextField
            label="Gateway name"
            isRequired={true}
            value={gatewayName}
            onChange={(e) => {
              setGatewayName(e.target.value);
              setError(null);
            }}
          />
        </div>
        {BenchlingConfig.INSTRUMENTS_ENABLE_S3_IAM_ROLE_ACCESS_METHOD && (
          <div className={classes.spacer}>
            <div className={classes.sectionLabel}>Access method</div>
            <div className={classes.permissionSelector}>
              <SegmentedControl
                aria-label="Access method"
                data={SEGMENTED_CONTROL_DATA}
                value={permissionMode}
                onChange={(value: string) => {
                  setPermissionMode(value as S3PermissionMode);
                  setError(null);
                }}
                fullWidth={true}
              />
            </div>
            {permissionMode === S3PermissionMode.IAM_ROLE && (
              <div>
                To connect to your S3 bucket, provide your IAM role ARN and bucket name. Benchling will assume
                this role to access your bucket. Use &quot;Generate trust policy&quot; and &quot;Generate
                permissions policy&quot; to get the policies to apply to your IAM role.
              </div>
            )}
            {permissionMode === S3PermissionMode.BUCKET_POLICY && (
              <div>
                To connect to your S3 bucket, provide your bucket name and apply the generated policy to your
                bucket. This grants Benchling&apos;s role direct access.
              </div>
            )}
          </div>
        )}
        <div className={classes.spacer}>
          {fields == null ? (
            <CenteredHorizontalThrobber />
          ) : (
            <>
              <GatewayFieldsForm
                fields={visibleFields}
                initialValues={stringFields(gateway.fields)}
                errorFieldId={error?.fieldId}
                onValuesChange={(values) => {
                  setFieldValues(values);
                  setError(null);
                }}
              />
              <div className={classes.readOnlyField}>
                {isIamMode ? (
                  <TextField
                    label="Benchling role ARN"
                    value={getGlobalRoleArn()}
                    disabled={true}
                    onChange={() => {}}
                  />
                ) : (
                  <TextField label="Benchling role ARN" value={roleArn} disabled={true} onChange={() => {}} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
